import React, { forwardRef } from 'react';

export type SvgIconProps = {
  /**
   * The color of the background
   * Only relevant with icons which have a default background color, such as 'Note'
   */
  backgroundColor?: string;
  children?: React.ReactNode;
  className?: string;
  ['data-test']?: string;

  /** a temporary fix due to new svg icons using a fill, but old svg icons using a stroke */
  ['data-temporary-fix']?: boolean;

  /**
   * The color the svg will appear.  The defaults are the original colors of the
   * svg.
   *
   * <div style="background-color: #E6FBFF; padding: 10px;">
   *  **NOTE:**
   *
   * 1. Will not work when `isColorInherited` is `true`
   * 2. Will not work on colored icons
   * </div>
   * */
  color?: string;
  fill?: string;
  /** The height of the svg */
  height?: number | string;
  /**
   * Make the svg the same color as the surrounding text.<br />
   * We don't automatically use surrounding color to ensure backwards
   * compatibility
   *
   * <div style="background-color: #E6FBFF; padding: 10px;">
   *  **NOTE:**
   *
   * 1. For dynamic text color, use `isColorInherited` over `color`
   *    *   EG: Links and Buttons (color changes when disabled/readonly)
   * 2. Will not work on colored icons
   * </div>
   * */
  isColorInherited?: boolean;
  opacity?: number | string;
  /** Optional accessibility props */
  role?: string;
  style?: React.CSSProperties;
  tabIndex?: number;
  title?: string;
  titleId?: string;
  viewBox?: string;
  /** The width of the svg */
  width?: number | string;
};

/**
 * Svg Icon component
 */
export const SvgIcon = forwardRef(function SvgIcon(
  { width = 24, height = 24, role, title, titleId, children, ...props }: SvgIconProps,
  ref: React.ForwardedRef<SVGSVGElement>,
) {
  return (
    <svg
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      ref={ref}
      width={width}
      height={height}
      aria-labelledby={titleId}
      role={role}
      viewBox={`0 0 ${width} ${height}`}
      {...props}
    >
      {title && <title id={titleId}>{title}</title>}
      {children}
    </svg>
  );
});
